import { useMemo, useRef } from 'react';
import { Box, BoxProps } from '@mui/material';
import { CheckIcon } from '@/src/components/icon/stroke/CheckIcon';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';

export const flowStepIconClasses = {
  root: 'FlowStepIcon-root',
  active: 'FlowStepIcon-active',
  completed: 'FlowStepIcon-completed',
  error: 'FlowStepIcon-error',
  disabled: 'FlowStepIcon-disabled',
};

export const FlowStepLabelIcon = ({ active, completed, error, ownerState }) => {
  const { disabled } = ownerState ?? {};
  // obtain icon based on the state
  const IconComponent = useMemo(() => {
    if (error) {
      return CloseIcon;
    } else if (completed && !active) {
      return CheckIcon;
    } else {
      return null;
    }
  }, [error, completed, active]);

  return (
    <FlowStepIconRoot
      className={[
        flowStepIconClasses.root,
        active && flowStepIconClasses.active,
        completed && flowStepIconClasses.completed,
        error && flowStepIconClasses.error,
        disabled && flowStepIconClasses.disabled,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {IconComponent && (
        <IconComponent
          sx={{
            position: 'absolute',
            fontSize: 'inherit',
            color: 'background.primary',
          }}
        />
      )}
    </FlowStepIconRoot>
  );
};

const FlowStepIconRoot = ({ children, className, sx, ...restProps }: BoxProps) => {
  // during first render (and first state) do not play animation
  const animation = useRef(false);
  const initialClassName = useRef(className);
  if (!animation.current && initialClassName.current !== className) {
    animation.current = true;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '28px',
        width: '28px',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        fontSize: '16px',
        '& .MuiSvgIcon-root': {
          zIndex: '5',
        },
        [`@keyframes flowStepperActive`]: {
          '0%': {
            transform: 'scale(1.5)',
            opacity: '0.8',
          },
          '100%': {
            transform: 'scale(1)',
            opacity: '1',
          },
        },
        [`@keyframes flowStepperCompleted`]: {
          '0%': {
            transform: 'scale(.8)',
            opacity: '0.8',
          },
          '100%': {
            transform: 'scale(1)',
            opacity: '1',
          },
        },
        // style is also applied in stepButton.theme.ts
        [`& .${flowStepIconClasses.root}`]: {
          height: '100%',
          width: '100%',
          borderRadius: '40px',
          borderColor: 'onSurface.os500',
          backgroundColor: 'transparent',
          borderWidth: '2px',
          borderStyle: 'solid',
          color: 'transparent',
          minHeight: '20px',
          // completed
          [`&.${flowStepIconClasses.completed}:not(.${flowStepIconClasses.active})`]: {
            color: 'onSurface.os700',
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary'),
            border: 'none',
            height: '100%',
            width: '100%',
            animation: animation.current ? 'flowStepperCompleted 0.4s' : 'none',
          },
          // error
          [`&.${flowStepIconClasses.error}, &.${flowStepIconClasses.error}.${flowStepIconClasses.completed}`]: {
            borderColor: 'error.main',
            backgroundColor: 'error.main',
          },
          // disabled style is applied from stepper.theme.ts as it has to be applied only for the non-linear variant
          // active
          [`&.${flowStepIconClasses.active}`]: {
            color: 'onSurface.os700',
            height: '100%',
            width: '100%',
            marginRight: '0px',
            marginLeft: '0px',
            borderColor: (theme) => (theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary'),
            '&::before': {
              content: '""',
              position: 'absolute',
              borderRadius: '40px',
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary'),
              inset: '5px',
              animation: animation.current ? 'flowStepperActive 0.4s' : 'none',
              zIndex: '1',
            },
            [`&.${flowStepIconClasses.error}`]: {
              borderColor: 'error.main',
              '&::before': {
                backgroundColor: 'transparent.transparent',
                border: 3,
                inset: '2px',
                borderColor: 'background.primary',
              },
            },
          },
        },
        ...sx,
      }}
      {...restProps}
    >
      {children}
      <Box className={className} />
    </Box>
  );
};
